import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toCapitalCase(str: string | null): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function generateCommitteeSlug(chamber: string | null, name: string): string {
  // Normalize the name first:
  // 1. Collapse spaces around hyphens (e.g., "State -Tribal" → "State-Tribal")
  // 2. Collapse multiple spaces
  const normalizedName = name
    .replace(/\s+-/g, '-') // Remove spaces before hyphens
    .replace(/-\s+/g, '-') // Remove spaces after hyphens
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();

  // Replace hyphens in name with double-hyphens, then convert spaces to single hyphens
  const encodedName = normalizedName
    .toLowerCase()
    .replace(/-/g, '--') // Escape existing hyphens as double-hyphens
    .replace(/\s+/g, '-'); // Replace spaces with single hyphens

  if (!chamber) return encodedName;
  return `${chamber.toLowerCase()}-${encodedName}`;
}

/**
 * Parse a committee URL slug back into chamber and name
 */
export function parseCommitteeSlug(slug: string): { chamber: string; name: string } | null {
  if (!slug) return null;

  // Find first single hyphen (chamber separator) - must not be part of double-hyphen
  const firstHyphenMatch = slug.match(/^([^-]+)-(.+)$/);
  if (!firstHyphenMatch) return null;

  const chamber = firstHyphenMatch[1].toLowerCase();
  const encodedName = firstHyphenMatch[2];

  // Decode: double-hyphens → single hyphens, single hyphens → spaces
  // Use a unique placeholder that won't appear in committee names
  const placeholder = '___HYPHEN___';
  const name = encodedName
    .replace(/--/g, placeholder) // Temporary placeholder for double-hyphens
    .replace(/-/g, ' ') // Single hyphens → spaces
    .replace(new RegExp(placeholder, 'g'), '-'); // Restore double-hyphens as single hyphens

  return { chamber, name };
}

/**
 * Capitalize a day abbreviation or name
 */
function capitalizeDay(day: string): string {
  const dayMap: Record<string, string> = {
    mon: 'Mon',
    monday: 'Monday',
    tue: 'Tue',
    tues: 'Tue',
    tuesday: 'Tuesday',
    wed: 'Wed',
    weds: 'Wed',
    wednesday: 'Wednesday',
    thu: 'Thu',
    thur: 'Thu',
    thurs: 'Thu',
    thus: 'Thu',
    thursday: 'Thursday',
    fri: 'Fri',
    friday: 'Friday',
    sat: 'Sat',
    saturday: 'Saturday',
    sun: 'Sun',
    sunday: 'Sunday',
  };
  return dayMap[day.toLowerCase()] || day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
}

/**
 * Format a meeting day string with proper capitalization
 */
function formatMeetingDay(day: string): string {
  const normalized = day.trim().toLowerCase();

  // Handle special cases that don't need pluralization
  if (normalized === 'on call' || normalized === 'on calls') return 'On Call';
  if (normalized === 'daily' || normalized === 'dailys') return 'Daily';

  // Handle slash-separated days (Mon/Wed, Tue/Thu)
  if (normalized.includes('/')) {
    return normalized
      .split('/')
      .map((d) => capitalizeDay(d.trim()))
      .join('/');
  }

  // Standard weekdays - add 's' for plural
  const capitalizedDay = capitalizeDay(normalized);
  return `${capitalizedDay}s`;
}

/**
 * Format meeting schedule text from day and time fields
 * Handles edge cases like "Daily", "On Call", and slash-separated days
 */
export function formatMeetingSchedule(
  meetingDay: string | null,
  meetingTime: string | null
): string {
  if (!meetingDay || !meetingTime) return 'Not specified';

  const normalizedDay = meetingDay.trim().toLowerCase();
  const normalizedTime = meetingTime.trim().toLowerCase();

  // Special case: "On Call" in both fields should just display "On Call"
  if (normalizedDay.includes('on call') && normalizedTime.includes('on call')) {
    return 'On Call';
  }

  // Format the meeting day
  const formattedDay = formatMeetingDay(meetingDay);

  return `${formattedDay} at ${meetingTime}`;
}
